import { useQuery } from '@tanstack/react-query';

import ListBlock from '../../../../common/components/ListBlock';
import BuyableProductTeaser, {
    BuyableProductTeaserSkeleton,
} from '../../../../common/components/molecules/BuyableProductTeaser';
import PageProperties from '../../../../common/components/molecules/PageProperties';
import { useAppSelector } from '../../../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../../../common/selectors/storeSelectors';
import searchFlow from '../../../../search/flow/searchFlow';
import { useProductSectionsContext } from '../../../contexts/productSectionsContext';
import { useCurrentSectionParams } from '../../../hooks/useCurrentSectionParams';
import { useVerticalProductTeaserContainer } from '../../../hooks/useProductTeaserContainer';
import SectionBreadcrumbs from '../productSectionPage/SectionBreadcrumbs';

const NotFoundPage = (props: { type: 'product' | 'section' }) => {
    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const { sectionTree } = useProductSectionsContext();
    const { currentSectionId, currentSectionPath, currentSection, sectionTrail } =
        useCurrentSectionParams(sectionTree);

    const searchFunction = () => {
        if (currentSectionId || sectionTrail.length) {
            return searchFlow.searchProductsInSection(
                {
                    name: 'categoryIds',
                    value: (currentSectionId || sectionTrail.slice(-1)[0].id).toString(),
                },
                24,
                0,
                [],
                null,
                [],
                currentProductionUnit,
                true,
                currentSection
                    ? {
                          name: 'Subcategory',
                          value: currentSection.name,
                          details: currentSectionPath,
                      }
                    : undefined,
            );
        }
        return searchFlow.searchProductOffersLoop({
            page: 0,
            resultsPerPage: 24,
            storeId: currentProductionUnit,
            sortBy: [],
            facets: [],
        });
    };

    const { data: relatedProducts } = useQuery({
        queryKey: ['ProductNotFound:Replacements'],
        enabled: true,
        queryFn: searchFunction,
        maxPages: 0,
        retry: false,
    });
    const { gridAdditionalClasses, cellsInRow, orientation } =
        useVerticalProductTeaserContainer('list');

    return (
        <div>
            <PageProperties
                headData={{
                    title: 'Produkten hittades ej',
                    link: {},
                    meta: {
                        robots: 'noindex, nofollow',
                        'prerender-status-code': '404',
                    },
                }}
                pageTypeNameForAnalytics="produktsida"
            />
            <div className="u-marginBsm">
                <SectionBreadcrumbs sectionTrail={sectionTrail} />
            </div>
            <div className="u-marginBlg u-paddingA u-bgGreenLight  u-borderRadius20  u-flex u-flexAlignItemsCenter u-flexJustifyContentCenter u-flexGap16 u-flexJustifySpaceBetween">
                <div>
                    <h1>
                        {props.type === 'product' ? 'Produkten' : 'Kategorin'} kunde inte hittas.
                    </h1>
                    <p>
                        Vi kunde inte hitta sidan som du söker, kanske är den flyttad? Prova ladda
                        om sidan om du tycker att något verkar helt fel.
                    </p>
                </div>
            </div>
            {relatedProducts && relatedProducts.items?.length && (
                <ListBlock
                    cellsInRow={cellsInRow}
                    gridClasses={gridAdditionalClasses}
                    items={relatedProducts.items}
                    itemUniqueKey={(item) => item.identifier.toString()}
                    renderItem={(item, _index) => (
                        <BuyableProductTeaser
                            orientation={orientation}
                            product={item}
                            isAsyncLink
                            analyticsOptions={{
                                list: 'Product not found',
                                listPosition: 0,
                                position: _index,
                            }}
                        />
                    )}
                    isLoading={false}
                    renderSkeleton={() => (
                        <BuyableProductTeaserSkeleton orientation={orientation} />
                    )}
                    analyticsListName=""
                    useSwiper={false}
                    horizontalHidden={false}
                />
            )}
        </div>
    );
};

export default NotFoundPage;
